import { onMounted, ref, watch, type Ref } from 'vue';
import { useFlowRuleName, useI18n } from '@/composables';
import { FlowHierarchyTypeEnum, StatusEnum } from '@/services/models/Enums';
import { cloneDeep } from 'lodash';

const treeLeafs = [
  {
    index: 'landings',
    type: FlowHierarchyTypeEnum.landing,
    i18nValue: 'landings',
    i18nDefault: 'Landings',
  },
];

export const useLpPixelTreeview = (
  flow: () => Record<string, unknown> | undefined,
  selectedLanding: Ref<TreeViewItem | null>,
) => {
  const { t } = useI18n();
  const { buildFlowRuleName, isOppositeFlowRulePredicate } = useFlowRuleName();

  const count = ref(0);
  const flowRegistry = ref<Record<number, string>>({});
  const flowMap = ref<TreeViewItem[]>([]);

  const isTreeNodeLanding = (node: TreeViewItem) =>
    node.type === FlowHierarchyTypeEnum.landing;

  const isTreeNodeLandingActive = (node: TreeViewItem) =>
    isTreeNodeLanding(node) && selectedLanding.value?.id === node.id;

  const handleSelectLanding = (node: TreeViewItem) => {
    if (isTreeNodeLanding(node)) {
      selectedLanding.value = node;
    }
  };

  const createTree = (
    data: Array<Record<string, unknown>>,
    type: FlowHierarchyTypeEnum = FlowHierarchyTypeEnum.rule,
    meta: Record<number, number> = {},
  ): TreeViewItem[] => {
    const result: TreeViewItem[] = [];

    data
      .filter((item) => item.status !== StatusEnum.inactive)
      .forEach((item, index) => {
        count.value = count.value + 1;
        const element: TreeViewItem = {
          id: count.value,
          name: item.name as string,
          type,
          children: [],
          conditions: item.conditions
            ? (buildFlowRuleName(
                { conditions: item.conditions as Record<string, unknown> },
                false,
              ) as FlowCondition[])
            : null,
          meta: cloneDeep(meta),
        };

        element.meta[type] = index;

        const paths = item.paths as Array<Record<string, unknown>> | undefined;

        if (paths?.length) {
          element.children = createTree(paths, FlowHierarchyTypeEnum.path, element.meta);
        } else if (item.directLinking) {
          count.value = count.value + 1;
          element.children.push({
            id: count.value,
            name: t('Direct linking is enabled in this path'),
            type: FlowHierarchyTypeEnum.utility,
            children: [],
          });
        } else {
          treeLeafs.forEach((subItem) => {
            const landingList = item[subItem.index] as Array<Record<string, unknown>> | undefined;

            if (landingList?.length) {
              landingList.forEach((landingItem, landingIndex) => {
                if (landingItem.id !== '') {
                  count.value = count.value + 1;
                  const landing: TreeViewItem = {
                    ...(landingItem as TreeViewItem),
                    id: count.value,
                    type: subItem.type,
                    name: landingItem.nameFull as string,
                    children: [],
                  };

                  landing.meta = cloneDeep(element.meta);
                  landing.meta[subItem.type] = landingIndex;
                  flowRegistry.value[landing.id] = landingItem.id as string;
                  element.children?.push(landing);
                }
              });
            }
          });
        }

        result.push(element);
      });

    return result;
  };

  const rebuildFlowMap = () => {
    const flowValue = flow();
    flowRegistry.value = {};
    count.value = 0;

    const rules = flowValue?.rules as Array<Record<string, unknown>> | undefined;
    flowMap.value = rules ? createTree(rules) : [];
  };

  watch(flow, rebuildFlowMap, { deep: true });

  onMounted(() => {
    rebuildFlowMap();
  });

  return {
    t,
    flowMap,
    isOppositeFlowRulePredicate,
    isTreeNodeLanding,
    isTreeNodeLandingActive,
    handleSelectLanding,
  };
};
